import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router';

import { api, unwrap, type Schemas } from '../api/client.js';
import { track } from '../api/events.js';
import { MarkdownExport } from '../components/ExportButtons.js';
import { FixPanel } from '../components/FixPanel.js';
import { HelpButton } from '../components/HelpButton.js';
import { useRecentlyFixed } from '../components/Improvements.js';
import { useLocale, useOverview } from '../components/site.js';
import { useMediaQuery } from '../components/useMediaQuery.js';
import {
  Button,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  SelectField,
  SeverityBadge,
  UrgencyBadge,
  type Severity,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDate, formatDateTime } from '../i18n.js';
import { useMe } from '../session.js';
import { SEVERITIES } from './Overview.js';

export const CATEGORIES = [
  'availability',
  'security',
  'links',
  'seo',
  'performance',
  'email',
  'compliance',
] as const;
type Category = (typeof CATEGORIES)[number];
const STATUSES = ['open', 'ignored', 'resolved'] as const;
type Status = (typeof STATUSES)[number];
type IgnoreReason = 'not_relevant' | 'will_fix_later' | 'false_positive';
const REASONS: IgnoreReason[] = ['not_relevant', 'will_fix_later', 'false_positive'];
type Group = Schemas['FindingGroup'];
type Urgency = 'now' | 'soon' | 'when_possible' | 'tip';

const oneOf = <T extends string>(value: string | null, allowed: readonly T[]): T | undefined =>
  allowed.find((a) => a === value);

/** When to act when a problem has no explanation (it carries its own urgency otherwise). */
const URGENCY_OF: Record<Severity, Urgency> = {
  critical: 'now',
  high: 'soon',
  medium: 'soon',
  low: 'when_possible',
  info: 'tip',
};

const urgencyOf = (g: Group): Urgency => g.explanation?.urgency ?? URGENCY_OF[g.severity];

/** The path of an address, short enough for a heading ("Ignorieren: /team"). */
const placeOf = (url: string) => {
  try {
    const u = new URL(url);
    return `${u.pathname}${u.search}`;
  } catch {
    return url;
  }
};

/** "1 Stelle · seit 10.10.2026": places only where a problem sits on pages, not the whole site. */
function useSpec(group: Group) {
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const since = group.items.reduce<string | null>(
    (min, f) => (min === null || f.firstSeenAt < min ? f.firstSeenAt : min),
    null,
  );
  const places = group.count > 1 || group.items.some((f) => f.affectedUrl !== null);
  return [
    places ? t('findings.count', { count: group.count }) : null,
    since ? t('findings.since', { date: formatDate(since, i18n.language, timeZone) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** The numbers of each status, from the same endpoint (plan SP-UX10.3, decision 7). */
function useStatusCounts(siteId: string, category: Category | undefined) {
  const results = useQueries({
    queries: STATUSES.map((status) => ({
      queryKey: ['site', siteId, 'findings', 'total', status, category ?? null],
      queryFn: () =>
        unwrap(
          api.GET('/sites/{siteId}/findings', {
            params: {
              path: { siteId },
              query: { status, limit: 1, ...(category ? { category } : {}) },
            },
          }),
        ).then((r) => r.total),
    })),
  });
  return (status: Status) => results[STATUSES.indexOf(status)]?.data;
}

/** One affected place: the address, ignoring it with a reason, or bringing it back. */
function Occurrence({
  finding,
  status,
  locked,
}: {
  finding: Schemas['Finding'];
  status: Status;
  locked: boolean;
}) {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const client = useQueryClient();
  const name = useId();
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState<IgnoreReason>('not_relevant');
  const change = useMutation({
    mutationFn: (body: { status: 'ignored' | 'open'; ignoreReason?: IgnoreReason }) =>
      unwrap(
        api.PATCH('/findings/{findingId}', {
          params: { path: { findingId: finding.id } },
          body,
        }),
      ),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['site', siteId] });
      // The counter in the menu and the site card come from the site list.
      await client.invalidateQueries({ queryKey: ['sites'] });
    },
  });
  const url = finding.affectedUrl;
  const place = url ?? t(locked ? 'findings.lockedPlace' : 'findings.wholeSite');
  return (
    <li className={asking ? 'asking' : undefined}>
      {asking ? (
        <form
          className="ignore-form"
          onSubmit={(e) => {
            e.preventDefault();
            change.mutate({ status: 'ignored', ignoreReason: reason });
          }}
        >
          <fieldset>
            <legend className="h4s">
              {t('findings.ignoreFor', { place: url ? placeOf(url) : place })}
            </legend>
            <div className="opts">
              {REASONS.map((r) => (
                <label key={r}>
                  <input
                    type="radio"
                    name={name}
                    value={r}
                    checked={reason === r}
                    onChange={() => setReason(r)}
                  />
                  <span>{t(`findings.reasons.${r}`)}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <p className="bodyM">{t('findings.ignoreNote')}</p>
          <div className="acts">
            <Button type="submit" size="sm" busy={change.isPending}>
              {t('findings.ignoreConfirm')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setAsking(false)}>
              {t('app.cancel')}
            </Button>
          </div>
        </form>
      ) : (
        <>
          <span className={status === 'open' ? undefined : 'done'}>
            {place}
            {status === 'ignored'
              ? ` · ${
                  finding.ignoreReason
                    ? t('findings.ignoredBecause', {
                        reason: t(`findings.reasons.${finding.ignoreReason}`),
                      })
                    : t('findings.ignored')
                }`
              : null}
          </span>
          {status === 'open' ? (
            <Button variant="ghost" size="sm" onClick={() => setAsking(true)}>
              {t('findings.ignore')}
            </Button>
          ) : null}
          {status === 'ignored' ? (
            <Button
              variant="secondary"
              size="sm"
              busy={change.isPending}
              onClick={() => change.mutate({ status: 'open' })}
            >
              {t('findings.restore')}
            </Button>
          ) : null}
        </>
      )}
      {change.isError ? <Notice kind="error">{errorMessage(change.error, t)}</Notice> : null}
    </li>
  );
}

/** A value of the API's evidence as text. */
const evidence = (value: unknown) =>
  typeof value === 'string'
    ? value
    : Array.isArray(value)
      ? value.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join(', ')
      : JSON.stringify(value);

/** Only what the API reports: the rule, the last check and the evidence per place (decision 7). */
function TechDetails({ group, status }: { group: Group; status: Status }) {
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const last = group.items.reduce<string | null>(
    (max, f) => (max === null || f.lastSeenAt > max ? f.lastSeenAt : max),
    null,
  );
  const withContext = group.items.filter((f) => f.context && Object.keys(f.context).length > 0);
  return (
    <details>
      <summary>{t('findings.tech')}</summary>
      <div className="body">
        <p className="mono">
          {t('findings.techRule', { rule: group.ruleCode })}
          {/* A fixed problem was last seen then, not checked. */}
          {last && status !== 'resolved'
            ? ` · ${t('findings.techChecked', { time: formatDateTime(last, i18n.language, timeZone) })}`
            : null}
        </p>
        {withContext.length > 0 ? (
          <ul className="mono">
            {withContext.map((f) => (
              <li key={f.id}>
                {Object.entries(f.context ?? {})
                  .map(([key, value]) => `${key}: ${evidence(value)}`)
                  .join(' · ')}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </details>
  );
}

/** The selected problem (site-findings.html .finding): what, why, what to do, the places. */
function Problem({
  group,
  status,
  verified,
  focus,
}: {
  group: Group;
  status: Status;
  verified: boolean;
  /**
   * On a phone the problem stands under the list: each pick – or a link naming it – moves there
   * (decision 4); 0 leaves the focus where it is.
   */
  focus: number;
}) {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const titleId = useId();
  const title = useRef<HTMLHeadingElement>(null);
  const spec = useSpec(group);
  const e = group.explanation;
  useEffect(() => {
    if (focus > 0) title.current?.focus();
  }, [focus]);
  // A locked weakness (T-101): the notice says what and what to do; why it matters stays.
  const questions = !e
    ? []
    : group.guidance === 'locked'
      ? ([['impact', e.impact]] as const)
      : ([
          ['what', e.what],
          ['impact', e.impact],
          ['action', e.action],
        ] as const);
  return (
    <article className="card finding" aria-labelledby={titleId}>
      <div>
        <div className="acts">
          <SeverityBadge severity={group.severity} />
          <UrgencyBadge urgency={urgencyOf(group)} />
          {spec ? <span className="spec">{spec}</span> : null}
        </div>
        <h2 id={titleId} ref={title} tabIndex={-1} className="h3s">
          {group.title}
        </h2>
      </div>
      {group.guidance === 'locked' ? (
        <Notice
          kind="warning"
          title={t('findings.lockedTitle')}
          action={
            <LinkButton to={`/sites/${siteId}/settings#verify`} variant="primary" size="sm">
              {t('findings.lockedCta')}
            </LinkButton>
          }
        >
          {t('findings.lockedNote')}
        </Notice>
      ) : null}
      {questions.some(([, text]) => text) ? (
        <div className="q">
          {questions.map(([key, text]) =>
            text ? (
              <div key={key}>
                <b>{t(`findings.${key}`)}</b>
                <p>{text}</p>
              </div>
            ) : null,
          )}
        </div>
      ) : null}
      {e?.legalDisclaimer ? <p className="note">{e.legalDisclaimer}</p> : null}
      {e && e.diySteps.length > 0 ? (
        <details
          onToggle={(ev) => {
            if (ev.currentTarget.open) track('diy_steps_opened', { ruleCode: group.ruleCode });
          }}
        >
          <summary>{t('findings.diy')}</summary>
          <div className="body">
            <ol>
              {e.diySteps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          </div>
        </details>
      ) : null}
      {status === 'open' && group.guidance === 'full' ? (
        <FixPanel siteId={siteId} ruleCode={group.ruleCode} />
      ) : null}
      {group.guidance === 'summary' ? (
        <div className="upsell">
          <span>{t('findings.summaryOnly')}</span>
          <LinkButton to="/billing" variant="secondary" size="sm">
            {t('findings.summaryCta')}
          </LinkButton>
        </div>
      ) : null}
      {group.service && status === 'open' ? (
        <div className="acts">
          <HelpButton
            siteId={siteId}
            service={group.service}
            {...(group.items[0] ? { findingId: group.items[0].id } : {})}
            ruleCode={group.ruleCode}
          />
        </div>
      ) : null}
      <div>
        <h3 className="h4s">{t('findings.affected')}</h3>
        <ul className="urls">
          {group.items.map((f) => (
            <Occurrence
              key={f.id}
              finding={f}
              status={status}
              locked={group.guidance === 'locked'}
            />
          ))}
        </ul>
        {group.count > group.items.length ? (
          <p className="note">{t('findings.more', { count: group.count - group.items.length })}</p>
        ) : null}
      </div>
      {/* Not for Free's summary (no places) nor a locked weakness (T-101). */}
      {verified && group.guidance === 'full' ? <TechDetails group={group} status={status} /> : null}
    </article>
  );
}

/** One entry of the list: the problem, its area, places and since when, its severity. */
function ProblemLink({
  group,
  current,
  search,
  onPick,
}: {
  group: Group;
  current: boolean;
  search: string;
  onPick: () => void;
}) {
  const { t } = useTranslation();
  const spec = useSpec(group);
  return (
    <Link
      to={{ search }}
      replace
      preventScrollReset
      aria-current={current ? 'true' : undefined}
      onClick={onPick}
    >
      <div>
        <b>{group.title}</b>
        <small>{[t(`site.tiles.${group.category}`), spec].filter(Boolean).join(' · ')}</small>
      </div>
      <SeverityBadge severity={group.severity} />
    </Link>
  );
}

/** The problems by urgency, the selected one marked (site-findings.html .flist). */
function ProblemList({
  groups,
  selected,
  onPick,
}: {
  groups: readonly Group[];
  selected: string;
  onPick: (ruleCode: string) => void;
}) {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const sections = (['now', 'soon', 'when_possible', 'tip'] as const)
    .map((u) => [u, groups.filter((g) => urgencyOf(g) === u)] as const)
    .filter(([, list]) => list.length > 0);
  const searchFor = (ruleCode: string) => {
    const next = new URLSearchParams(params);
    next.set('open', ruleCode);
    return `?${next.toString()}`;
  };
  return (
    <nav className="flist" aria-label={t('findings.title')}>
      {sections.map(([u, list]) => (
        <div key={u}>
          <h2 className="grp">
            {t(`report.urgency.${u}`)} · {list.length}
          </h2>
          <ul>
            {list.map((g) => (
              <li key={g.ruleCode}>
                <ProblemLink
                  group={g}
                  current={g.ruleCode === selected}
                  search={searchFor(g.ruleCode)}
                  onPick={() => onPick(g.ruleCode)}
                />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

/** "Alle 24 · Kritisch 1 · Hoch 2 …": one level at a time (T-84; plan SP-UX10.3, decision 7). */
function SeverityChips({
  groups,
  active,
  onPick,
}: {
  groups: readonly Group[];
  active: Severity | undefined;
  onPick: (severity: Severity | undefined) => void;
}) {
  const { t } = useTranslation();
  const total = groups.reduce((n, g) => n + g.count, 0);
  const per = (s: Severity) =>
    groups.filter((g) => g.severity === s).reduce((n, g) => n + g.count, 0);
  return (
    <div className="chips" role="group" aria-label={t('findings.filters.severity')}>
      <button type="button" aria-pressed={!active} onClick={() => onPick(undefined)}>
        <span>{t('findings.filters.all')}</span> <span className="n">{total}</span>
      </button>
      {SEVERITIES.filter((s) => per(s) > 0).map((s) => (
        <button
          key={s}
          type="button"
          aria-pressed={active === s}
          onClick={() => onPick(active === s ? undefined : s)}
        >
          <span>{t(`severity.${s}`)}</span> <span className="n">{per(s)}</span>
        </button>
      ))}
    </div>
  );
}

/** How many problems are open, how many urgent, how many got fixed lately (30 days or the plan's). */
function Summary({ siteId }: { siteId: string }) {
  const { t } = useTranslation();
  const open = useOverview(siteId).data?.openFindings;
  const { days, fixed } = useRecentlyFixed(siteId);
  if (!open) return null;
  const total = open.critical + open.high + open.medium + open.low + open.info;
  return (
    <div className="sub">
      <span>{t('findings.sum.open', { n: total })}</span>{' '}
      <span>{t('findings.sum.urgent', { n: open.critical + open.high })}</span>{' '}
      {fixed !== null ? <span>{t('findings.sum.fixed', { n: fixed, count: days })}</span> : null}
    </div>
  );
}

/** S4 "Probleme" (site-findings.html): the list by urgency and the selected problem beside it. */
export function Findings() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const locale = useLocale();
  const [params, setParams] = useSearchParams();
  const category = oneOf(params.get('category'), CATEGORIES);
  const severity = oneOf(params.get('severity'), SEVERITIES);
  const status = oneOf(params.get('status'), STATUSES) ?? 'open';
  const openRule = params.get('open');
  const phone = useMediaQuery('(max-width: 56.25rem)');
  // How often a problem was picked; a link naming one (?open=) counts as the first pick.
  const [picks, setPicks] = useState(() => (params.has('open') ? 1 : 0));
  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('open');
    setPicks(0);
    setParams(next, { replace: true });
  };
  const findings = useQuery({
    queryKey: ['site', siteId, 'findings', category, status, locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/findings', {
          params: {
            path: { siteId },
            query: {
              grouped: 'true',
              status,
              locale,
              limit: 200,
              ...(category ? { category } : {}),
            },
          },
        }),
      ),
  });
  const count = useStatusCounts(siteId, category);
  const verified = useOverview(siteId).data?.site.verified === true;
  const all = findings.data?.groups ?? [];
  // Severity filters on the client: the chips show how many problems each level has (T-84).
  const groups = severity ? all.filter((g) => g.severity === severity) : all;
  const selected = groups.find((g) => g.ruleCode === openRule) ?? groups[0];

  return (
    <>
      <div className="hd">
        <div>
          <h1>{t('findings.title')}</h1>
          <Summary siteId={siteId} />
        </div>
        <div className="acts">
          <MarkdownExport siteId={siteId} size="sm" />
          <LinkButton to={`/sites/${siteId}/report`} variant="ghost" size="sm">
            {t('site.report')}
          </LinkButton>
        </div>
      </div>

      <div className="filters">
        <div className="row">
          <div className="seg" role="group" aria-label={t('findings.filters.status')}>
            {STATUSES.map((s) => {
              const n = count(s);
              return (
                <button
                  key={s}
                  type="button"
                  aria-pressed={status === s}
                  onClick={() => setFilter('status', s === 'open' ? '' : s)}
                >
                  {t(`findings.statuses.${s}`)}
                  {n === undefined ? null : ` · ${n}`}
                </button>
              );
            })}
          </div>
          <SelectField
            label={t('findings.filters.category')}
            value={category ?? ''}
            onChange={(e) => setFilter('category', e.target.value)}
          >
            <option value="">{t('findings.filters.allAreas')}</option>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {t(`site.tiles.${c}`)}
              </option>
            ))}
          </SelectField>
        </div>
        {all.length > 0 ? (
          <SeverityChips
            groups={all}
            active={severity}
            onPick={(value) => setFilter('severity', value ?? '')}
          />
        ) : null}
      </div>

      {findings.isPending ? <Loading /> : null}
      {findings.isError ? (
        <LoadError
          message={errorMessage(findings.error, t)}
          onRetry={() => void findings.refetch()}
        />
      ) : null}
      {findings.data && groups.length === 0 ? (
        <div className="empty">
          <b>{t(`findings.empty.${status}`)}</b>
          {status === 'open' ? <span>{t('findings.emptyNext')}</span> : null}
        </div>
      ) : null}
      {selected ? (
        <div className="fx">
          <ProblemList
            groups={groups}
            selected={selected.ruleCode}
            onPick={(ruleCode) => {
              track('finding_viewed', { ruleCode });
              setPicks((n) => n + 1);
            }}
          />
          <Problem
            key={`${selected.ruleCode}-${status}`}
            group={selected}
            status={status}
            verified={verified}
            focus={phone ? picks : 0}
          />
        </div>
      ) : null}
    </>
  );
}
